/**
 * Collection (Category) page JS.
 *
 * Xử lý cho card category grid:
 *   1. Qty stepper (−/+) trong card
 *   2. Add-to-cart → /cart/add.js + dispatch ink:cart:updated (cart-count.js refresh)
 *   3. AJAX stock + campaign hydrate GỘP 1 CALL: batch /api/v1/stock?include=presents
 *      (StockProxy + present count — mỗi call đi 4 hop qua App Proxy nên
 *      gộp thay vì 2 endpoint riêng, docs perf §B2) → fill badge 在庫あり/なし +
 *      当日出荷 (当日出荷 = in_stock && !reserve, browse_6_new_cart.php) + strip
 *      「今だけ さらにN本プレゼント」. out-of-stock → disable add.
 *      Skeleton loading (giống toppage): badge 在庫 + khu mua hàng SSR ô xám pulsing
 *      → applyStock fill data thật; API fail → applyStockFallback fail-open.
 *   4. Load-more もっと見る: fetch ?view=ajax-products → append card + hydrate stock card mới.
 *
 * Stock public không cần token; presents cần token (nếu có) để exclude FO/ASOSHI/PAN.
 * Fail-silent.
 */
(function () {
  'use strict';

  const PROXY_BASE = ((window.INK_API_CONFIG && window.INK_API_CONFIG.proxyBase) || '/apps/ink').replace(/\/$/, '');
  const STOCK_URL = PROXY_BASE + '/api/v1/stock';
  const STOCK_BATCH_MAX = 50; // StockController limit

  /**
   * Token CHỈ dùng khi biết chắc nó thuộc customer đang login.
   *
   * File này chạy TRƯỚC `api-client.js` (nó load cuối `theme.liquid`, còn file này load
   * trong section) nên không thể dựa vào `window.inkApi`. `api-client` cố ý KHÔNG
   * xoá token có chủ khác khỏi localStorage (xoá là phá phiên tab đang sống),
   * nên đọc thô `localStorage.ink_api_token` có thể lấy token của customer khác: server
   * chặn bằng `SESSION_MISMATCH` nên không rò dữ liệu, nhưng request thành 401 và badge
   * 在庫/campaign mất im lặng. Vì vậy tự kiểm owner ở đây — cùng luật `api-client.js`.
   */
  function ownedToken() {
    const cfg = window.INK_API_CONFIG || {};
    const id = cfg.customer && cfg.customer.id;
    if (!id) return null; // guest → không gắn token nào

    if (window.inkApi && window.inkApi.token) return window.inkApi.token;

    const owner = (cfg.shopDomain || '') + '|' + id;
    if (localStorage.getItem('ink_api_token_owner') !== owner) return null;

    return localStorage.getItem('ink_api_token');
  }

  // Token (nếu logged-in) — campaign present cần để exclude FO/ASOSHI/PAN.
  // Guest: không token → không exclude (đúng Joomla). Stock không cần token (public).
  function authHeaders() {
    const token = ownedToken();
    const h = { Accept: 'application/json', 'ngrok-skip-browser-warning': '1' };
    if (token) h['X-Ink-Token'] = token;
    return h;
  }

  // Báo category-filter.js re-apply (điều kiện 在庫あり phụ thuộc stock AJAX).
  // Debounce 1 frame → nhiều applyStock trong 1 batch chỉ refresh 1 lần.
  let refreshScheduled = false;
  function scheduleFilterRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    (window.requestAnimationFrame || setTimeout)(() => {
      refreshScheduled = false;
      document.dispatchEvent(new CustomEvent('ink:filter:refresh'));
    });
  }

  // ===== 1. Qty stepper =====
  function onStepperClick(e) {
    const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const stepper = btn.closest('[data-qty-stepper]');
    const input = stepper && stepper.querySelector('[data-qty-input]');
    if (!input) return;
    const cur = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;
    const next = btn.hasAttribute('data-qty-plus') ? cur + 1 : cur - 1;
    input.value = String(Math.min(max, Math.max(min, next)));
  }

  // ===== 2. Add to cart =====
  async function onAddToCart(e) {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const card = btn.closest('[data-category-card]');
    const input = card && card.querySelector('[data-qty-input]');
    const quantity = input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = '追加中...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });
      if (!res.ok) throw new Error('cart/add ' + res.status);
      document.dispatchEvent(new CustomEvent('ink:cart:updated'));
      document.dispatchEvent(new CustomEvent('ink:cart:added')); // mở modal COMMON (đồng nhất site)
      btn.textContent = '追加しました';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
    } catch (err) {
      console.error('[collection] add to cart', err);
      btn.textContent = original;
      btn.disabled = false;
    }
  }

  // ===== 3. Stock + campaign present hydrate (1 call gộp) =====
  async function hydrateStock(cards) {
    const list = cards.filter((c) => c.dataset.sku);
    // Card không SKU (variant thiếu sku) → không bao giờ có data, bỏ skeleton ngay
    cards.filter((c) => !c.dataset.sku).forEach(applyStockFallback);
    const skus = [...new Set(list.map((c) => c.dataset.sku))];
    if (!skus.length) return;

    // Present đi kèm cùng call (`include=presents` — server tính từ cùng tập SKU).
    // Tắt khi category excluded (Joomla shop.browse.php:128).
    const grid = document.querySelector('[data-collection-grid]');
    const withPresents = !!grid && grid.dataset.legacyCategoryId !== '2890';

    const presents = {};
    let campaignLink = null;
    // Cờ 対象商品 chung cả trang lấy từ card đầu (Joomla $product_skus[0]) — chunk sau
    // gửi kèm để server không lấy SKU đầu của chunk.
    const presentFlagSku = skus[0];

    for (let i = 0; i < skus.length; i += STOCK_BATCH_MAX) {
      const chunk = skus.slice(i, i + STOCK_BATCH_MAX);
      let json;
      let items;
      try {
        // Adopt request bắn SỚM từ inline script cuối section (chạy ngay lúc parse
        // HTML, không đợi file JS này tải xong — perf §C″). Chỉ nhận khi đúng tập
        // SKU + cùng cờ presents; dùng 1 lần.
        const early = window.__inkStockEarly;
        if (early && early.skus === chunk.join(',') && early.presents === withPresents) {
          delete window.__inkStockEarly;
          json = await early.promise;
        } else {
          const url = STOCK_URL + '?skus=' + encodeURIComponent(chunk.join(','))
            + (withPresents
              ? '&include=presents&present_flag_sku=' + encodeURIComponent(presentFlagSku)
              : '');
          // Kèm token khi lấy presents (exclude FO/ASOSHI/PAN); stock thuần
          // không cần token (public).
          const res = await fetch(url, {
            headers: withPresents
              ? authHeaders()
              : { Accept: 'application/json', 'ngrok-skip-browser-warning': '1' },
            credentials: 'same-origin',
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          json = await res.json();
        }
        items = json && json.data && json.data.items;
      } catch (err) {
        console.warn('[collection] stock fetch failed', err);
        // Fail-open: bỏ skeleton + hiện nút mua các card chưa resolve (idempotent,
        // card đã applyStock thành no-op). Guard mua thật = /stock/validate.
        list.forEach(applyStockFallback);
        return;
      }
      if (!items) {
        list
          .filter((c) => chunk.indexOf(c.dataset.sku) !== -1)
          .forEach(applyStockFallback);
        continue;
      }
      list
        .filter((c) => chunk.indexOf(c.dataset.sku) !== -1)
        .forEach((c) => applyStock(c, items[c.dataset.sku]));

      if (withPresents && json.data) {
        if (json.data.presents) Object.assign(presents, json.data.presents);
        if (json.data.present_link) campaignLink = json.data.present_link;
      }
    }

    if (withPresents) {
      list.forEach((card) => {
        if (card.hasAttribute('data-no-present')) return;
        const count = presents[card.dataset.sku] || 0;
        if (count > 0) applyCampaign(card, count, campaignLink);
      });
    }
  }

  function applyStock(card, stock) {
    const inStock = !!stock && stock.status === 'in_stock';
    // buy_enable=false = 販売終了/完売 (jos_vm_cart_close, live từ /stock batch).
    // Mặc định true khi thiếu/fail-open (không chặn oan).
    const buyEnable = !stock || stock.buy_enable !== false;
    // Mua được = còn hàng VÀ chưa 販売終了.
    const purchasable = inStock && buyEnable;
    // Cờ filter 在庫あり (category-filter.js đọc dataset.fStock) — 完売 cũng = không mua được
    card.dataset.fStock = purchasable ? '1' : '0';
    scheduleFilterRefresh();
    const stockBadge = card.querySelector('[data-stock-badge]');
    const samedayBadge = card.querySelector('[data-sameday-badge]');
    const addBtn = card.querySelector('[data-add-to-cart]');

    // Swap ảnh placeholder 1:1 Joomla getImageUrlForEmpty (ps_product.php:4217-4234,
    // call site browse :6024/:6028): 完売(!buyEnable)→soldout ưu tiên > 欠品(!stock)→
    // stockout. stockEnable = getStockEnough = stock>0 THUẦN, KHÔNG miễn 予約 (không
    // call site Joomla nào exempt reserve). Ngoại lệ checkChangeImage
    // (jos_vm_change_images, :4212) → keep_image=true GIỮ ảnh gốc, override swap.
    const keepImage = !!stock && stock.keep_image === true;
    const cardImg = card.querySelector('[data-card-img]');
    if (cardImg && ! keepImage) {
      if (! buyEnable && card.dataset.soldoutImg) {
        cardImg.src = card.dataset.soldoutImg;
      } else if (! inStock && card.dataset.stockoutImg) {
        cardImg.src = card.dataset.stockoutImg;
      }
    }

    // Bỏ skeleton khu mua hàng — control/在庫なし hiện theo nhánh dưới
    const purchaseSkeleton = card.querySelector('[data-purchase-skeleton]');
    if (purchaseSkeleton) purchaseSkeleton.remove();

    if (stockBadge) {
      // textContent thay luôn skeleton span SSR bên trong. Badge CHỈ theo tồn kho,
      // 1:1 Joomla (browse_category_element_cache.tpl.php:52-56: `stockCheck` →
      // 在庫あり/在庫なし, không có nhánh nào in 販売終了). `jos_vm_cart_close` gộp
      // cả 完売 lẫn 販売終了 nên không suy ra được hàng ngừng bán thật — dán nhãn
      // 販売終了 lên hàng chỉ đang 欠品 là nói sai với khách. Việc chặn mua vẫn giữ:
      // nhánh `! buyEnable` dưới ẩn khu mua + hiện ただいま販売しておりません.
      stockBadge.textContent = inStock ? '在庫あり' : '在庫なし';
      // Màu inline (tránh Tailwind JIT không thấy class runtime)
      stockBadge.style.color = inStock ? '#0f9438' : '#999999';
    }
    // 当日出荷 = còn hàng && không phải予約商品 (reserve_flg) && chưa 販売終了
    if (samedayBadge) {
      const reserve = card.dataset.reserve === '1';
      samedayBadge.classList.toggle('hidden', !(purchasable && !reserve));
    }
    // CHỈ 販売終了/完売 (buy_enable=false) mới ẩn khu mua hàng.
    // 欠品/在庫なし VẪN mua được — 1:1 Joomla: card category render
    // `$form_addtocart` KHÔNG phụ thuộc stock (browse_6_new_cart.php: stockCheck
    // chỉ đổi badge 在庫あり/なし + 当日出荷), và nhánh đổi nút sang 通知 trong
    // addtocart_form_new.tpl.php gate bằng `CHECK_STOCK == '1'` mà config
    // production để RỖNG (virtuemart.cfg.php:108) → không bao giờ chạy.
    // Chặn mua thật (nếu cần) là ở server: /stock/validate.
    const purchaseControl = card.querySelector('[data-purchase-control]');
    const soldout = card.querySelector('[data-soldout]');
    if (! buyEnable) {
      if (purchaseControl) purchaseControl.classList.add('hidden');
      if (soldout) {
        soldout.classList.remove('hidden');
        soldout.classList.add('flex');
      }
      if (addBtn) addBtn.disabled = true;
    } else {
      // Còn bán (kể cả 在庫なし) → hiện control, nút enable (phòng card tái dùng)
      if (purchaseControl) purchaseControl.classList.remove('hidden');
      if (soldout) soldout.classList.add('hidden');
      if (addBtn) addBtn.disabled = false;
    }
  }

  // Fail-open khi stock API lỗi / không có data: bỏ skeleton + hiện nút mua
  // (mirror hành vi cũ — guard mua thật ở /stock/validate).
  // Idempotent: card đã resolve (skeleton đã mất) → no-op, không đụng 在庫なし đã set.
  function applyStockFallback(card) {
    const skel = card.querySelector('[data-purchase-skeleton]');
    if (skel) {
      skel.remove();
      const purchaseControl = card.querySelector('[data-purchase-control]');
      if (purchaseControl) purchaseControl.classList.remove('hidden');
    }
    const stockBadge = card.querySelector('[data-stock-badge]');
    if (stockBadge && stockBadge.querySelector('.animate-pulse')) stockBadge.textContent = '';
    // Fail-open: coi như còn hàng cho filter 在庫あり (không chặn user — guard thật ở server).
    card.dataset.fStock = '1';
    scheduleFilterRefresh();
  }

  // ===== 3b. Campaign present banner (data đến từ hydrateStock include=presents) =====
  function applyCampaign(card, count, link) {
    const strip = card.querySelector('[data-campaign-strip]');
    const text = card.querySelector('[data-campaign-text]');
    if (!strip || !text) return;
    text.textContent = '今だけ さらに' + count + '本プレゼント！';
    strip.classList.remove('hidden', 'invisible');
    strip.classList.add('flex');
    // Link campaign detail (Joomla wrap <a href={clink}>)
    if (link) {
      strip.dataset.href = link;
      strip.style.cursor = 'pointer';
    }
  }

  // ===== 4. Load more =====
  // Lọc B2B (category-product-visible) chạy SAU paginate → 1 trang có thể không còn card
  // nào hiện. Gặp trang rỗng thì tải tiếp tới khi có card hoặc hết trang.
  async function loadNext(btn) {
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = '読み込み中...';
    try {
      const grid = document.querySelector('[data-grid-items]');
      let nextUrl = btn.dataset.nextUrl;
      let added = 0;
      while (nextUrl && added === 0) {
        const sep = nextUrl.indexOf('?') !== -1 ? '&' : '?';
        const res = await fetch(nextUrl + sep + 'view=ajax-products', { headers: { Accept: 'text/html' } });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCards = Array.from(doc.querySelectorAll('[data-category-card]'));

        if (grid && newCards.length) {
          const frag = document.createDocumentFragment();
          newCards.forEach((c) => frag.appendChild(c));
          grid.appendChild(frag);
          hydrateStock(newCards);
          scheduleFilterRefresh();
          added = newCards.length;
        }

        const nextBtn = doc.querySelector('[data-load-more]');
        nextUrl = nextBtn && nextBtn.dataset.nextUrl ? nextBtn.dataset.nextUrl : '';
      }

      if (nextUrl) {
        btn.dataset.nextUrl = nextUrl;
        btn.textContent = original;
        btn.disabled = false;
      } else {
        const wrap = btn.closest('[data-load-more-wrap]');
        (wrap || btn).remove();
      }
    } catch (err) {
      console.error('[collection] load more', err);
      btn.textContent = original;
      btn.disabled = false;
    }
  }

  function onLoadMore(e) {
    const btn = e.target.closest('[data-load-more]');
    if (!btn || btn.hasAttribute('hidden') || btn.disabled) return;
    e.preventDefault();
    if (!btn.dataset.nextUrl) return;
    loadNext(btn);
  }

  // ===== Boot =====
  function init() {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid) return;

    document.addEventListener('click', (e) => {
      onStepperClick(e);
      onAddToCart(e);
      onLoadMore(e);
      // Campaign strip → campaign detail (E)
      const strip = e.target.closest('[data-campaign-strip][data-href]');
      if (strip) window.location.href = strip.dataset.href;
    });

    const cards = Array.from(grid.querySelectorAll('[data-category-card]'));
    hydrateStock(cards);

    const moreBtn = grid.querySelector('[data-load-more]');
    if (!cards.length && moreBtn && moreBtn.dataset.nextUrl) loadNext(moreBtn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
